import { FocusKeyManager, FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { getDecade } from 'date-fns';
import { Subject } from 'rxjs';

import { headerActionHandlerMethods, getLabeledDateUnits } from '../utils/helpers';
import { HEADER_ACTION_HANDLERS, labeledDateUnitType, IHeaderConfig } from '../utils/types';
import { BLACK } from './../../../helpers/colors';
import { KeyboardKeys } from './../../../helpers/KeyboardKeys.enum';
import { ChevronLeft, ChevronLeftDouble, ChevronRight, ChevronRightDouble } from './../../icon/icon-map';
import { DATE_PICKER_MODE } from '../utils/constants';
import { PhxDatePickerService } from '../date-picker.service';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../../locale/phx-locale.token';
import { PhxStatsigService } from '../../../services/phx-statsig.service';
import { PhxFocusableItemDirective } from '../phx-focusable-item.directive';
import { takeUntil } from 'rxjs/operators';
import { PHX_STATSIG_KEYS } from '../../../constants';

type handlerType = HEADER_ACTION_HANDLERS;

@Component({
  selector: 'phx-date-picker-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-date-picker-header',
    tabIndex: '0',
    '(focus)': 'onWrapperFocus()',
  },
})
export class PhxDatePickerHeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly handlers: typeof HEADER_ACTION_HANDLERS = HEADER_ACTION_HANDLERS;
  public readonly prefixClass: string = 'phx-date-picker-header';
  public iconColor = BLACK;
  public ChevronLeftDoubleIcon: string = ChevronLeftDouble;
  public ChevronLeftIcon: string = ChevronLeft;
  public ChevronRightDoubleIcon: string = ChevronRightDouble;
  public ChevronRightIcon: string = ChevronRight;
  public DATE_PICKER_MODE = DATE_PICKER_MODE;
  public startingYearOfDecade: string;
  public endingYearOfDecade: string;
  public phxDatePickerToggleEnabled: boolean = false;
  @Input() public monthFormat: string = 'MMM';
  @Input() public yearFormat: string = 'yyyy';
  @Input() public value: Date;
  @Input() public selections: labeledDateUnitType;
  @Input() public config: IHeaderConfig = {};
  @Input() public mode: DATE_PICKER_MODE;

  @Output() readonly updateCalendar = new EventEmitter<Date>();
  @Output() readonly updateMode = new EventEmitter<DATE_PICKER_MODE>();

  @ViewChildren(PhxFocusableItemDirective)
  private focusableItems: QueryList<PhxFocusableItemDirective>;
  private keyManager: FocusKeyManager<PhxFocusableItemDirective>;
  private _statsigServiceDestroy$ = new Subject<void>();

  constructor(
    private focusMonitor: FocusMonitor,
    private statsigService: PhxStatsigService,
    private cdr: ChangeDetectorRef,
    private phxDatePickerService: PhxDatePickerService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {}

  ngAfterViewInit(): void {
    this.keyManager = new FocusKeyManager(this.focusableItems)
      .withHorizontalOrientation('ltr')
      .withVerticalOrientation(false)
      .withWrap();
    this.keyManager.setFirstItemActive();
    this.focusableItems.forEach((item: PhxFocusableItemDirective) => {
      this.focusMonitor.monitor(item.elementRef.nativeElement, true);
    });
  }

  ngOnDestroy(): void {
    this._statsigServiceDestroy$.next();
    this._statsigServiceDestroy$.complete();
  }

  private updateSelections(): void {
    if (this.value) {
      this.selections = getLabeledDateUnits(
        this.value,
        { year: this.yearFormat, month: this.monthFormat },
        this.phxDatePickerService.getDateFnsLocale(),
      );

      if (this.phxLocale?.formatMonth) {
        this.selections.month.label = this.phxLocale.formatMonth(this.value);
      }

      this.setupDecadeYears();
    }
  }

  private setupDecadeYears(): void {
    this.startingYearOfDecade = getDecade(this.value).toString();
    const fullYear = parseInt(this.startingYearOfDecade) + 9;
    this.endingYearOfDecade = fullYear.toString().slice(-2);
  }

  public onKeydown(event: KeyboardEvent, handler: handlerType): void {
    if (event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.SPACE) {
      event.preventDefault();
      if (handler === this.handlers.YEAR_BUTTON) this.toggleMode(DATE_PICKER_MODE.YEAR);
      else if (handler === this.handlers.MONTH_BUTTON) this.toggleMode(DATE_PICKER_MODE.MONTH);
      else this.moveCalendar(handler);
    } else if (event.code !== KeyboardKeys.TAB) {
      event.preventDefault();
      this.keyManager.onKeydown(event);
    }
  }

  public onWrapperFocus(): void {
    this.keyManager.setFirstItemActive();
  }

  public ngOnInit(): void {
    if (!this.value) this.value = new Date();
    this.updateSelections();
    this.statsigService
      .getConfigs()
      .pipe(takeUntil(this._statsigServiceDestroy$))
      .subscribe(configs => {
        this.phxDatePickerToggleEnabled = configs[PHX_STATSIG_KEYS.PHX_DATE_PICKER_TOGGLE_ENABLED] ?? false;
        this.cdr.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { value } = changes;
    if (value) this.updateSelections();
  }

  public moveCalendar(to: handlerType): void {
    this.value = headerActionHandlerMethods[to](this.value, this.mode);
    this.updateCalendar.emit(this.value);
    this.updateSelections();
  }

  public toggleMode(mode: DATE_PICKER_MODE): void {
    this.mode = mode;
    this.updateMode.emit(mode);
  }

  public resolveClass(actionType: string): string {
    let className = this.prefixClass;
    switch (actionType) {
      case this.handlers.PREV_BUTTON:
        className += '-prev-year-btn';
        if (this.config?.hideYear) {
          className += '-hidden';
        } else if (this.config?.disableYear || this.config?.disablePrevYear) className += '-disabled';
        break;
      case this.handlers.PREV_MONTH:
        className += '-prev-month-btn';
        if (this.config?.hideMonth) {
          className += '-hidden';
        } else if (this.config?.disableMonth || this.config?.disablePrevMonth) className += '-disabled';
        break;
      case this.handlers.NEXT_MONTH:
        className += '-next-month-btn';
        if (this.config?.hideMonth) {
          className += '-hidden';
        } else if (this.config?.disableMonth || this.config?.disableNextMonth) className += '-disabled';
        break;
      case this.handlers.NEXT_BUTTON:
        className += '-next-year-btn';
        if (this.config?.hideYear) {
          className += '-hidden';
        } else if (this.config?.disableYear || this.config?.disableNextYear) className += '-disabled';
        break;
    }
    return className;
  }
}
